import { MapPin, Radio, Route, QrCode, Compass, Music2, Briefcase, Calendar, Languages, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useEffect, useRef, useState } from 'react';
import { HeroMapSoundCity } from './HeroMapSoundCity';
import { landingTranslations } from '../contexts/landingTranslations';

type LandingPageProps = {
  onGetStarted: () => void;
};

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const { language, setLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);

  const t = landingTranslations[language as keyof typeof landingTranslations] || landingTranslations.en;

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/MapSound_Logo_Text_Horizontal_W.png"
              alt="MapSound"
              className="h-11 md:h-17 w-auto"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>

          <button
            onClick={onGetStarted}
            className="hidden md:block px-6 py-2.5 bg-[#00c7b7] text-gray-900 text-sm font-bold rounded-lg hover:bg-[#14f0d5] hover:shadow-lg hover:shadow-[#00c7b7]/50 transition-all duration-300"
          >
            {t.nav.tryButton}
          </button>
        </div>
      </nav>

      {/* Mobile Floating Button */}
      <button
        onClick={onGetStarted}
        className="md:hidden fixed bottom-6 right-6 z-50 px-8 py-4 bg-[#00c7b7] text-gray-900 text-base font-bold rounded-full shadow-2xl shadow-[#00c7b7]/50 hover:scale-105 transition-all duration-300"
      >
        {t.nav.tryButton}
      </button>

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_#00c7b7_0%,_transparent_50%)]"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-[#00c7b7] to-white bg-clip-text text-transparent">
              {t.hero.title}
            </h1>
            <p className="text-2xl md:text-4xl font-bold text-white mb-4">
              {t.hero.subtitle}
            </p>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
              {t.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={onGetStarted}
                className="px-8 py-4 bg-[#00c7b7] text-gray-900 text-lg font-bold rounded-xl hover:bg-[#14f0d5] hover:shadow-2xl hover:shadow-[#00c7b7]/50 transition-all duration-300"
              >
                {t.hero.tryButton}
              </button>
              <a
                href="mailto:info@mapsound.me?subject=Project Inquiry&body=Hi, I'm interested in discussing a project with MapSound."
                className="px-8 py-4 border-2 border-[#00c7b7] text-[#00c7b7] text-lg font-bold rounded-xl hover:bg-[#00c7b7]/10 transition-all duration-300 inline-block text-center"
              >
                {t.hero.contactButton}
              </a>
            </div>
          </div>

          <HeroMapSoundCity />
        </div>
      </section>

      {/* Intro Section */}
      <section id="intro" data-animate className={`py-20 px-6 transition-all duration-1000 ${isVisible.intro ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            {t.intro.title}
          </h2>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
            {t.intro.description}
          </p>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" data-animate className={`py-20 px-6 transition-all duration-1000 ${isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="container mx-auto max-w-6xl">
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300 hover:shadow-xl hover:shadow-[#00c7b7]/20">
                <div className="w-14 h-14 bg-[#00c7b7]/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <MapPin className="w-7 h-7 text-[#00c7b7]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{t.features.soundDrops.title}</h3>
                <p className="text-gray-300 leading-relaxed">
                  {t.features.soundDrops.description}
                </p>
              </div>

              <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300 hover:shadow-xl hover:shadow-[#00c7b7]/20">
                <div className="w-14 h-14 bg-[#00c7b7]/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Radio className="w-7 h-7 text-[#00c7b7]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{t.features.soundZones.title}</h3>
                <p className="text-gray-300 leading-relaxed">
                  {t.features.soundZones.description}
                </p>
              </div>

              <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300 hover:shadow-xl hover:shadow-[#00c7b7]/20">
                <div className="w-14 h-14 bg-[#00c7b7]/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Route className="w-7 h-7 text-[#00c7b7]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{t.features.soundTrails.title}</h3>
                <p className="text-gray-300 leading-relaxed">
                  {t.features.soundTrails.description}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300 hover:shadow-xl hover:shadow-[#00c7b7]/20">
                <div className="w-14 h-14 bg-[#00c7b7]/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Compass className="w-7 h-7 text-[#00c7b7]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{t.features.listeningRadius.title}</h3>
                <p className="text-gray-300 leading-relaxed">
                  {t.features.listeningRadius.description}
                </p>
              </div>

              <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300 hover:shadow-xl hover:shadow-[#00c7b7]/20">
                <div className="w-14 h-14 bg-[#00c7b7]/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <QrCode className="w-7 h-7 text-[#00c7b7]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{t.features.qrAccess.title}</h3>
                <p className="text-gray-300 leading-relaxed">
                  {t.features.qrAccess.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" data-animate className={`py-20 px-6 bg-gray-900/50 transition-all duration-1000 ${isVisible.how ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-white">
            {t.howItWorks.title}
          </h2>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#00c7b7] to-cyan-400 text-gray-900 text-3xl font-bold rounded-2xl mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{t.howItWorks.step1.title}</h3>
              <p className="text-gray-300">
                {t.howItWorks.step1.description}
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-400 text-white text-3xl font-bold rounded-2xl mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{t.howItWorks.step2.title}</h3>
              <p className="text-gray-300">
                {t.howItWorks.step2.description}
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-400 text-white text-3xl font-bold rounded-2xl mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{t.howItWorks.step3.title}</h3>
              <p className="text-gray-300">
                {t.howItWorks.step3.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Use Cases */}
      <section id="usecases" data-animate className={`py-20 px-6 transition-all duration-1000 ${isVisible.usecases ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-white">
            {t.useCases.title}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300">
              <div className="w-14 h-14 bg-[#00c7b7]/20 rounded-xl flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-[#00c7b7]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{t.useCases.tourism.title}</h3>
              <p className="text-gray-300 leading-relaxed">
                {t.useCases.tourism.description}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-purple-500 transition-all duration-300">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{t.useCases.fitness.title}</h3>
              <p className="text-gray-300 leading-relaxed">
                {t.useCases.fitness.description}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-orange-400 transition-all duration-300">
              <div className="w-14 h-14 bg-orange-400/20 rounded-xl flex items-center justify-center mb-6">
                <Music2 className="w-7 h-7 text-orange-400" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{t.useCases.artCulture.title}</h3>
              <p className="text-gray-300 leading-relaxed">
                {t.useCases.artCulture.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section id="pricing" data-animate className={`py-20 px-6 bg-gray-900/50 transition-all duration-1000 ${isVisible.pricing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-white">
            {t.pricing.title}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-[#00c7b7] transition-all duration-300">
              <h3 className="text-2xl font-bold mb-2 text-white">{t.pricing.starter.name}</h3>
              <div className="text-4xl font-bold text-[#00c7b7] mb-6">{t.pricing.starter.price}</div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.starter.feature1}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.starter.feature2}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.starter.feature3}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border-2 border-[#00c7b7] shadow-xl shadow-[#00c7b7]/20 transform scale-105">
              <div className="inline-block px-3 py-1 bg-[#00c7b7] text-gray-900 text-sm font-bold rounded-full mb-4">
                {t.pricing.pro.popular}
              </div>
              <h3 className="text-2xl font-bold mb-2 text-white">{t.pricing.pro.name}</h3>
              <div className="text-4xl font-bold text-[#00c7b7] mb-6">{t.pricing.pro.price}<span className="text-lg text-gray-400">{t.pricing.pro.perMonth}</span></div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.pro.feature1}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.pro.feature2}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.pro.feature3}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00c7b7] mt-1">✓</span>
                  <span>{t.pricing.pro.feature4}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-purple-500 transition-all duration-300">
              <h3 className="text-2xl font-bold mb-2 text-white">{t.pricing.business.name}</h3>
              <div className="text-4xl font-bold text-purple-400 mb-6">{t.pricing.business.price}<span className="text-lg text-gray-400">{t.pricing.business.perMonth}</span></div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 mt-1">✓</span>
                  <span>{t.pricing.business.feature1}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 mt-1">✓</span>
                  <span>{t.pricing.business.feature2}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 mt-1">✓</span>
                  <span>{t.pricing.business.feature3}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 mt-1">✓</span>
                  <span>{t.pricing.business.feature4}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" data-animate className={`py-24 px-6 bg-gradient-to-br from-gray-900 via-[#00c7b7]/10 to-gray-900 transition-all duration-1000 ${isVisible.vision ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="container mx-auto max-w-4xl text-center">
          <Sparkles className="w-16 h-16 text-[#00c7b7] mx-auto mb-8" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            {t.vision.title}
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-4">
            {t.vision.subtitle}
          </p>
          <p className="text-lg text-gray-400">
            {t.vision.description}
          </p>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 px-6 bg-gray-900">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
            {t.cta.title}
          </h2>
          <button
            onClick={onGetStarted}
            className="px-10 py-4 bg-[#00c7b7] text-gray-900 text-lg font-bold rounded-xl hover:bg-[#14f0d5] hover:shadow-2xl hover:shadow-[#00c7b7]/50 transition-all duration-300 mb-8"
          >
            {t.cta.button}
          </button>
          <div className="flex justify-center gap-6 text-sm text-gray-400">
            <a href="#" className="hover:text-[#00c7b7] transition-colors">{t.footer.links.privacy}</a>
            <span>|</span>
            <a href="#" className="hover:text-[#00c7b7] transition-colors">{t.footer.links.terms}</a>
            <span>|</span>
            <a href="#" className="hover:text-[#00c7b7] transition-colors">{t.footer.links.contact}</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-3">
              <img
                src="/mapsound_logo_white.png"
                alt="MapSound"
                className="h-32 md:h-40"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>

            <div className="flex items-center gap-3">
              <Languages className="w-5 h-5 text-gray-400" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as any)}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-900 border border-gray-700 rounded-lg hover:bg-gray-800 hover:border-[#00c7b7] transition-colors cursor-pointer"
              >
                <option value="en">English</option>
                <option value="ja">日本語</option>
                <option value="ko">한국어</option>
              </select>
            </div>

            <div className="text-gray-500 text-sm text-center">
              {t.footer.copyright}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
